import { jsPDF } from 'jspdf';

export const generateConsentPDF = (client, studio) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const primaryColor = studio?.color || '#4F46E5'; // Default Indigo
    const studioName = studio?.studio_name?.toUpperCase() || 'BOOKINK STUDIO';

    let y = 0;

    // 1. HEADER WITH COLOR
    doc.setFillColor(primaryColor);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(studioName, 20, 20);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(studio?.address || '122 Cornwall St, Plymouth, PL1 1NF', 20, 28);

    // Studio Logo (if available)
    if (studio?.logo) {
        try {
            doc.addImage(studio.logo, 'PNG', 170, 5, 25, 25, undefined, 'FAST');
        } catch (e) {
            console.warn("Could not add logo to PDF", e);
        }
    }

    y = 55;

    // TITLE
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text('CONSENT FORM AGREEMENT', 105, y, { align: 'center' });

    // TIMESTAMP
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    const generatedDate = client.signed_at
        ? new Date(client.signed_at).toLocaleString()
        : new Date().toLocaleString();
    doc.text(`Generated on: ${generatedDate}`, 105, y + 6, { align: 'center' });

    y += 20;

    // RED FLAG WARNING BANNER (if has_red_flags is true)
    if (client.has_red_flags) {
        doc.setFillColor(254, 202, 202); // Light red background
        doc.roundedRect(20, y, 170, 12, 2, 2, 'F');
        doc.setTextColor(185, 28, 28); // Dark red text
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text('⚠️ ATTENTION: Health conditions require artist review before proceeding', 105, y + 8, { align: 'center' });
        y += 18;
    }

    // CLIENT DETAILS BOX - expanded height to fit ID photo
    const clientBoxHeight = client.id_photo ? 75 : 65;
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 250, 252); // Slate-50 like
    doc.roundedRect(20, y, 170, clientBoxHeight, 3, 3, 'FD');

    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("CLIENT DETAILS", 25, y + 8);

    // ID Photo thumbnail in client details box
    if (client.id_photo) {
        try {
            doc.addImage(client.id_photo, 'JPEG', 155, y + 12, 30, 40, undefined, 'FAST');
        } catch (e) {
            console.warn("Could not add ID photo thumbnail", e);
        }
    }

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");

    let dy = y + 18;
    const col1 = 30; // Labels
    const col2 = 80; // Values

    const row = (label, value) => {
        doc.setFont("helvetica", "bold");
        doc.text(`${label}:`, col1, dy);
        doc.setFont("helvetica", "normal");
        doc.text(String(value || '-'), col2, dy);
        dy += 7;
    };

    row("Full Name", `${client.first_name} ${client.middle_name || ''} ${client.last_name}`.replace(/\s+/g, ' ').trim());
    row("Email", client.email);
    row("Phone", client.phone);
    row("Date of Birth", client.dob ? new Date(client.dob).toLocaleDateString() : '-');
    row("Age", client.age);
    row("Address", `${client.address || ''}, ${client.postcode || ''}`.replace(/^,\s*|,\s*$/g, '') || '-');

    y = dy + (client.id_photo ? 20 : 10);

    // APPOINTMENT INFO
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("APPOINTMENT DETAILS", 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text(`Date: ${client.appt_date ? new Date(client.appt_date).toLocaleDateString() : '-'}`, 30, y);
    doc.text(`Artist: ${client.staff_name || 'Unassigned'}`, 100, y);
    doc.text(`Price: £${client.price || '0.00'}`, 160, y);

    y += 15;

    // AGREEMENTS
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("AGREEMENTS ACCEPTED", 20, y);
    y += 8;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");

    const rawPdfAgs = client.agreements;
    let pdfAgreements = [];
    if (Array.isArray(rawPdfAgs)) {
        pdfAgreements = rawPdfAgs;
    } else if (typeof rawPdfAgs === 'string') {
        try {
            pdfAgreements = JSON.parse(rawPdfAgs);
            if (!Array.isArray(pdfAgreements)) pdfAgreements = [pdfAgreements];
        } catch (e) {
            if (rawPdfAgs.trim().length > 0) pdfAgreements = [rawPdfAgs];
        }
    }

    if (pdfAgreements.length > 0) {
        pdfAgreements.forEach(item => {
            const title = typeof item === 'object' ? (item.title || '') : '';
            const content = typeof item === 'object' ? (item.content || '') : item;

            if (title) {
                doc.setFont("helvetica", "bold");
                doc.text(title.toUpperCase(), 20, y);
                y += 5;
            }

            doc.setFont("helvetica", "normal");
            const lines = doc.splitTextToSize(content, 170);
            if (y + lines.length * 4 > 275) { doc.addPage(); y = 20; }
            doc.text(lines, 20, y);
            y += lines.length * 4 + 4;
        });
    } else {
        doc.text("No specific agreements recorded.", 20, y);
        y += 6;
    }

    y += 6;

    // HEALTH CONDITIONS
    if (y + 30 > 275) { doc.addPage(); y = 20; }
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("HEALTH CONDITIONS", 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");

    let conditions = [];
    try {
        conditions = client.health_conditions && Array.isArray(client.health_conditions)
            ? client.health_conditions
            : (typeof client.health_conditions === 'string' && client.health_conditions.trim()
                ? JSON.parse(client.health_conditions)
                : ["None Declared"]);
    } catch (e) {
        conditions = ["None Declared"];
    }

    if (conditions.length > 0 && !(conditions.length === 1 && (conditions[0] === 'None of the above' || (typeof conditions[0] === 'object' && conditions[0].content === 'None of the above')))) {
        conditions.forEach(c => {
            const text = typeof c === 'object' ? (c.content || c.title) : c;
            doc.text(`☑ ${text}`, 25, y);
            y += 6;
        });
    } else {
        doc.text("None Declared", 25, y);
        y += 6;
    }

    // HEALTH DETAILS (if provided)
    if (client.health_details && client.health_details.trim()) {
        y += 4;
        doc.setFillColor(255, 251, 235); // Amber-50 background
        doc.setDrawColor(217, 119, 6); // Amber-600 border
        const detailLines = doc.splitTextToSize(client.health_details, 160);
        const boxHeight = Math.max(15, detailLines.length * 5 + 10);
        doc.roundedRect(20, y, 170, boxHeight, 2, 2, 'FD');

        doc.setFontSize(9);
        doc.setTextColor(146, 64, 14); // Amber-800 text
        doc.setFont("helvetica", "bold");
        doc.text("Additional Health Details:", 25, y + 6);
        doc.setFont("helvetica", "normal");
        doc.text(detailLines, 25, y + 12);
        y += boxHeight + 6;
    }

    y += 10;

    // MEDIA CONSENT SECTION
    if (y + 25 > 275) { doc.addPage(); y = 20; }
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("MEDIA CONSENT", 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");

    if (client.media_consent) {
        doc.setTextColor(22, 163, 74); // Green
        doc.text("✓ Customer has granted permission for photos/marketing use", 25, y);
        y += 6;
        if (client.instagram_handle) {
            doc.setTextColor(88, 80, 236); // Purple/Instagram-like
            doc.text(`Instagram: @${client.instagram_handle}`, 25, y);
            y += 6;
        }
    } else {
        doc.setTextColor(107, 114, 128); // Gray
        doc.text("✗ Customer has NOT granted media consent", 25, y);
        y += 6;
    }

    y += 10;

    // SIGNATURE
    if (y + 50 > 275) { doc.addPage(); y = 20; }
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y); // Separator
    y += 10;

    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont("helvetica", "bold");
    doc.text("SIGNED BY CUSTOMER", 20, y);

    if (client.signature) {
        try {
            doc.addImage(client.signature, 'PNG', 20, y + 5, 50, 25);
            y += 35;
        } catch (e) {
            doc.text("(Signature Error)", 20, y + 10);
            y += 20;
        }
    } else {
        doc.setTextColor(150, 150, 150);
        doc.text("(No Digital Signature)", 20, y + 10);
        y += 20;
    }

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);

    // Immutable Metadata Footer
    const appVersion = "BookInk v2.3 (Enterprise)"; // Hardcoded for compliance
    const genTime = new Date().toISOString();
    const finalIP = client.ip_address || 'Recorded by Server';

    // Single line metadata string
    const metadata = `Consent ID: ${client.consent_id || 'PENDING'} | IP: ${finalIP} | Signed: ${client.signed_at ? new Date(client.signed_at).toISOString() : 'Pending'} | Ver: ${appVersion} | Gen: ${genTime}`;

    // Position at absolute bottom of page to ensure it functions as a footer even if content is short
    doc.text(metadata, 105, 290, { align: 'center' });

    return doc;
};
